import React, { createContext, useContext, useState } from 'react';

const MeetingContext = createContext();

export const useMeeting = () => {
  const context = useContext(MeetingContext);
  if (!context) {
    throw new Error('useMeeting must be used within a MeetingProvider');
  }
  return context;
};

export const MeetingProvider = ({ children }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Base URL for API calls
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005/api';

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Helper function to get auth headers for file uploads
  const getAuthHeadersForUpload = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  // Fetch all meetings
  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/meetings`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setMeetings(data);
        return data;
      } else {
        throw new Error('Failed to fetch meetings');
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      // For demo purposes, return mock data
      const mockMeetings = [
        {
          id: 1,
          title: 'Weekly Team Meeting',
          description: 'Discuss weekly progress and objectives',
          meeting_date: '2024-01-25T10:00:00',
          location: 'Conference Room A',
          organizer_name: 'John Doe',
          status: 'scheduled',
          participant_count: 5
        },
        {
          id: 2,
          title: 'Budget Review',
          description: 'Review quarterly budget and expenses',
          meeting_date: '2024-01-26T14:00:00',
          location: 'Board Room',
          organizer_name: 'Jane Smith',
          status: 'completed',
          participant_count: 3
        }
      ];
      setMeetings(mockMeetings);
      return mockMeetings;
    } finally {
      setLoading(false);
    }
  };

  // Fetch single meeting with details
  const fetchMeetingById = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/meetings/${id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to fetch meeting');
      }
    } catch (error) {
      console.error('Error fetching meeting:', error);
      // Return mock data for demo
      return {
        id: id,
        title: 'Demo Meeting',
        description: 'This is a demo meeting',
        meeting_date: '2024-01-25T10:00:00',
        location: 'Conference Room',
        organizer_name: 'Demo User',
        status: 'scheduled',
        agenda: 'Demo agenda items',
        participants: [],
        attachments: [],
        notes: []
      };
    }
  };

  // Create new meeting
  const createMeeting = async (meetingData) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/meetings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(meetingData)
      });
      
      if (response.ok) {
        const newMeeting = await response.json();
        setMeetings(prev => [newMeeting.meeting, ...prev]);
        return newMeeting;
      } else {
        throw new Error('Failed to create meeting');
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
      // For demo purposes, add to local state
      const mockMeeting = {
        id: Date.now(),
        ...meetingData,
        organizer_name: 'Current User',
        participant_count: meetingData.participants?.length || 0,
        status: 'scheduled'
      };
      setMeetings(prev => [mockMeeting, ...prev]);
      return { meeting: mockMeeting };
    } finally {
      setLoading(false);
    }
  };

  // Update meeting
  const updateMeeting = async (id, updateData) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/meetings/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        setMeetings(prev => 
          prev.map(meeting => 
            meeting.id === id ? { ...meeting, ...updateData } : meeting
          )
        );
        return true;
      } else {
        throw new Error('Failed to update meeting');
      }
    } catch (error) {
      console.error('Error updating meeting:', error);
      // For demo purposes, update local state
      setMeetings(prev => 
        prev.map(meeting => 
          meeting.id === parseInt(id) ? { ...meeting, ...updateData } : meeting
        )
      );
      return true;
    } finally {
      setLoading(false);
    }
  };

  // Delete meeting
  const deleteMeeting = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/meetings/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setMeetings(prev => prev.filter(meeting => meeting.id !== id));
        return true;
      } else {
        throw new Error('Failed to delete meeting');
      }
    } catch (error) {
      console.error('Error deleting meeting:', error);
      // For demo purposes, remove from local state
      setMeetings(prev => prev.filter(meeting => meeting.id !== parseInt(id)));
      return true;
    } finally {
      setLoading(false);
    }
  };

  // Upload attachment
  const uploadAttachment = async (meetingId, file) => {
    try {
      const formData = new FormData();
      formData.append('attachment', file);

      const response = await fetch(`${API_BASE_URL}/meetings/${meetingId}/attachments`, {
        method: 'POST',
        headers: getAuthHeadersForUpload(),
        body: formData
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to upload attachment');
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      // For demo purposes, simulate success
      return { message: 'Attachment uploaded successfully (demo mode)' };
    }
  };

  // Delete attachment
  const deleteAttachment = async (meetingId, attachmentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/meetings/${meetingId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to delete attachment');
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      return { message: 'Attachment deleted successfully (demo mode)' };
    }
  };

  // Download attachment
  const downloadAttachment = (meetingId, attachmentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/meetings/${meetingId}/attachments/${attachmentId}/download?token=${token}`;
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Error downloading file (demo mode)');
    }
  };

  const value = {
    meetings,
    loading,
    fetchMeetings,
    fetchMeetingById,
    createMeeting,
    updateMeeting,
    deleteMeeting,
    uploadAttachment,
    deleteAttachment,
    downloadAttachment
  };

  return (
    <MeetingContext.Provider value={value}>
      {children}
    </MeetingContext.Provider>
  );
};
